import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { heroBanners } from "@/mocks/comics";

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 600);
    },
    [isTransitioning]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % heroBanners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const banner = heroBanners[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-700 ease-in-out"
        style={{
          backgroundImage: `url(${banner.image})`,
          opacity: isTransitioning ? 0.5 : 1,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-4 md:px-6 flex items-center">
        <div className="w-full max-w-2xl" style={{ opacity: isTransitioning ? 0 : 1, transition: "opacity 0.4s" }}>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50 mb-4">
            {banner.tag}
          </span>
          <h2
            className="text-2xl md:text-5xl font-bold text-white mb-4 leading-tight"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {banner.title}
          </h2>
          <p className="text-sm md:text-base text-white/80 mb-6 line-clamp-2 leading-relaxed max-w-lg">
            {banner.description}
          </p>
          <Link
            to={`/comic/${banner.comicSlug}`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-all duration-200 whitespace-nowrap"
          >
            立即阅读
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {heroBanners.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              i === current ? "w-6 bg-accent-500" : "bg-white/50 hover:bg-white/80"
            }`}
            aria-label={`第 ${i + 1} 张`}
          />
        ))}
      </div>

      <div className="absolute right-4 md:right-10 bottom-6 z-10 flex flex-col gap-3">
        {heroBanners.map((b, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-16 h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 hidden md:block ${
              i === current ? "border-accent-500 scale-110" : "border-white/30 opacity-60 hover:opacity-100"
            }`}
            aria-label={`切换到 ${b.title}`}
          >
            <img
              src={b.image}
              alt={b.title}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
    </section>
  );
}